'use client';
import { FC, useState, useEffect, useRef } from 'react';
import useBus, { dispatch } from '../../hooks/use-bus';
import { useTranslation } from '../../hooks/use-translation';
import { AppBusEventType } from '../../interfaces/bus-events';
import { OwApp } from '../../interfaces/domain/ow-app.modal';
import SpriteIcon from '../shared/sprite-icon';
import { useClassName } from '../../hooks/use-class-name';
import { MainDownloadBtn } from './main-dl-btn';
import { MultiDownloadList } from './multi-dl-list';
import { UninstallAppButton } from './uninstall-app-button';
import {
  useDownloadClickHandler,
  DownloadClickComponent,
} from '../../hooks/use-download-click-handler';
import { useInstalledExtensions } from '../../hooks/use-installed-extensions';
import { useDownloadInProgress } from '../../hooks/use-download-in-progress';
import { useOverwolfStore } from '../../hooks/use-overwolf-store';
import { useDownloadLabel } from '../../hooks/use-download-label';

interface DownloadItemProps {
  item: OwApp;
}
// -----------------------------------------------------------------------------

export const DownloadSection: FC<DownloadItemProps> = ({
  item,
}): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isInstalledMenuOpen, setIsInstalledMenuOpen] =
    useState<boolean>(false);
  const downloadBtn = useRef<HTMLAnchorElement>(null);

  const { handleClickDownload } = useDownloadClickHandler({
    item,
    component: DownloadClickComponent.OneApp,
  });

  const { isAvailable: isOverwolfStoreAvailable } = useOverwolfStore();
  const { installedAppIds, checkElectronAppInstalled } =
    useInstalledExtensions();

  const isInstalled = !item.isElectron && installedAppIds.has(item.id);
  // Per-download label: only the button whose own app is installed switches its
  // verb to launch (Electron or native). See useDownloadLabel.
  const getDownloadLabel = useDownloadLabel();

  // Inside the Overwolf client (iframe + installed) only Windows downloads are
  // runnable, so non-Windows download buttons are hidden there. On the web all
  // platforms are shown.
  const availableDownloads = (() => {
    const all = item.alternativeDownloads ?? [];
    return isOverwolfStoreAvailable
      ? all.filter((d) => d.platform === 'Windows')
      : all;
  })();

  // On opening an Electron app page, re-check its install state so the button
  // reflects the current on-disk reality (it may have been installed or removed
  // outside the store since the initial bulk fetch).
  useEffect(() => {
    if (item.isElectron) {
      void checkElectronAppInstalled(item.id);
    }
  }, [item.isElectron, item.id, checkElectronAppInstalled]);

  const orderedDownloads = (() => {
    const all = availableDownloads;
    if (!isInstalled || all.length < 2) {
      return all;
    }

    const matchIdx = all.findIndex((d) => d.appId === item.id);
    if (matchIdx <= 0) {
      return all;
    }

    const match = all[matchIdx];
    const originalMain = all[0];
    const rest = all.filter((_, i) => i !== 0 && i !== matchIdx);
    return [match, originalMain, ...rest];
  })();

  const mainDownload = orderedDownloads[0];

  // In-progress (download or native install) state. The main button tracks only
  // the main download's id; an alternative download lights up its own row in the
  // menu instead — see MultiDownloadList.
  const downloadInProgress = useDownloadInProgress();
  const isDownloading = downloadInProgress(mainDownload?.appId ?? item.id);

  const mainBtnLabel = getDownloadLabel(
    mainDownload,
    item.id,
    Boolean(item.isElectron),
  );
  // Electron downloads go through the shell's downloadApp (an installer file
  // download) rather than installApp — the in-progress button verb differs.
  const mainIsElectron = mainDownload?.isElectron ?? Boolean(item.isElectron);

  const mainDownloadUrl = mainDownload?.url ? new URL(mainDownload.url) : null;
  mainDownloadUrl?.searchParams.append('utm_content', 'new-light');
  mainDownloadUrl?.searchParams.append('utm_source', 'web_app_store');

  // ---------------------------------------------------------------------------
  const handleShowDownloadModal = (url: string, isDeepLink = false): void => {
    dispatch({
      type: AppBusEventType.App.ShowDownloadModal,
      data: {
        isOpen: true,
        currentApp: item,
        url,
        isDeepLink,
      },
    });
  };

  useBus(AppBusEventType.App.TriggerDownload, () => {
    downloadBtn.current.click();
    handleShowDownloadModal(downloadBtn.current.href);
  });

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (availableDownloads.length === 1 && !isInstalled) return;

    const handleCloseMenu = (e: any): void => {
      const elm = e.target as HTMLElement;
      if (
        elm.closest('.option-item-url') ||
        elm.closest('.option-item-btn') ||
        elm.closest('.multi-options-btn')
      )
        return;
      setIsOpen(false);
      setIsInstalledMenuOpen(false);
    };

    document.addEventListener('mousedown', handleCloseMenu);
    window.addEventListener('scroll', () => {
      setIsOpen(false);
      setIsInstalledMenuOpen(false);
    });

    return () => {
      document.removeEventListener('mousedown', handleCloseMenu);
      window.removeEventListener('scroll', () => {
        setIsOpen(false);
        setIsInstalledMenuOpen(false);
      });
    };
  }, [isInstalled]);

  // ---------------------------------------------------------------------------
  const multiClassName = useClassName(
    {
      'is-open': isOpen,
    },
    'multi-downloads-menu',
  );

  const installedMenuClassName = useClassName(
    {
      'is-open': isInstalledMenuOpen,
    },
    'multi-downloads-menu',
  );

  return (
    <div className="download-actions">
      {availableDownloads.length === 1 && !isInstalled && (
        <MainDownloadBtn
          appTitle={item.title}
          btnLabel={mainBtnLabel}
          url={mainDownloadUrl?.toString() ?? ''}
          className="dl-btn btn-primary"
          isLoading={isDownloading}
          isElectron={mainIsElectron}
          handleClickDownload={handleClickDownload}
          ref={downloadBtn}
        />
      )}

      {availableDownloads.length === 1 && isInstalled && (
        <div className={installedMenuClassName}>
          <div className="main-multi-btn">
            <MainDownloadBtn
              appTitle={item.title}
              btnLabel={mainBtnLabel}
              url={mainDownloadUrl?.toString() ?? ''}
              className="multi-dl-btn"
              isLoading={isDownloading}
              isElectron={mainIsElectron}
              handleClickDownload={handleClickDownload}
              ref={downloadBtn}
            />

            <button
              className="multi-options-btn"
              onClick={() => setIsInstalledMenuOpen(!isInstalledMenuOpen)}
            >
              <SpriteIcon icon="arrow-down" svgClassName="arrow-icon" />
            </button>
          </div>

          <ul className="multi-options-list installed-app-options">
            <UninstallAppButton extensionId={item.id} appName={item.title} />
          </ul>
        </div>
      )}

      {availableDownloads.length > 1 && (
        <div className={isInstalled ? installedMenuClassName : multiClassName}>
          <div className="main-multi-btn">
            <MainDownloadBtn
              appTitle={item.title}
              btnLabel={mainBtnLabel}
              url={mainDownloadUrl?.toString() ?? ''}
              className="multi-dl-btn"
              icon={mainDownload?.platform}
              isLoading={isDownloading}
              isElectron={mainIsElectron}
              handleClickDownload={handleClickDownload}
              ref={downloadBtn}
            />

            <button
              className="multi-options-btn"
              onClick={() =>
                isInstalled
                  ? setIsInstalledMenuOpen(!isInstalledMenuOpen)
                  : setIsOpen(!isOpen)
              }
              title={!isInstalled ? t('downloadOptions') : undefined}
            >
              <SpriteIcon icon="arrow-down" svgClassName="arrow-icon" />
            </button>
          </div>

          <MultiDownloadList
            appTitle={item.title}
            dlData={orderedDownloads}
            skipMain={true}
            mainAppId={item.id}
            itemIsElectron={Boolean(item.isElectron)}
            handleClickDownload={handleClickDownload}
            extraItem={
              isInstalled && (
                <UninstallAppButton
                  extensionId={item.id}
                  appName={item.title}
                />
              )
            }
          />
        </div>
      )}
    </div>
  );
};
